/**
 * gift-cards.service.ts
 *
 * Typed API call wrappers for the Gift Cards domain.
 * All methods use the shared apiClient (Axios instance with JWT interceptor).
 */
import { apiClient } from './apiClient';

// ─── Types ────────────────────────────────────────────────────────────────────

/**
 * What a card history row records (gift_cards_log.action, KTD13). Written by
 * every v4 and Prospera writer since the online gift cards work; rows written
 * before it carry a blank action and the detail modal guesses their label.
 */
export type GiftCardAction =
  | 'sale'
  | 'issue'
  | 'delivery'
  | 'resend'
  | 'recipient_change'
  | 'claim'
  | 'redeem'
  | 'restore'
  | 'refund'
  | 'void'
  | 'void_refund'
  | 'reload'
  | 'adjust'
  | 'edit'
  | 'view_code';

export type GiftCardTransactionType = 'issue' | 'redeem' | 'void' | 'reload' | GiftCardAction;

export interface GiftCard {
  giftCardId: number;
  accountId: number;
  serialNumber: string;
  title: string | null;
  originalValue: number;
  totalValue: number;
  redeemedAmount: number;
  currentBalance: number;
  issuedToPatientId: number | null;
  issuedToPatientName: string | null;
  issuedByUserId: number | null;
  purchaseDate: string | null;
  expiresDate: string | null;
  active: boolean;
  notes: string | null;
  createdAt: string;
  /**
   * True when the API masked serialNumber because this user may not see an
   * unclaimed online card's code (no Gift Credit permission, KTD19). False
   * means the full code was sent and GET /gift-cards/{id}/code may be called.
   */
  codeMasked?: boolean;
  // ── Online cards (sold through API v4). Null / false on desk cards. ──
  isOnline?: boolean;
  buyerPatientId?: number | null;
  buyerName?: string | null;
  buyerEmail?: string | null;
  buyerPhone?: string | null;
  recipientName?: string | null;
  recipientEmail?: string | null;
  message?: string | null;
  /** The buyer kept the card ("send it to me"): delivery goes to the buyer. */
  sendToSelf?: boolean;
  claimedPatientId?: number | null;
  claimedPatientName?: string | null;
  claimedDate?: string | null;
  deliveryStatus?: string | null;
  deliverySentDate?: string | null;
  /** The payment that sold the card, when it was recorded. */
  salePaymentId?: number | null;
}

/** Answer of GET /gift-cards/check/{code}: the card plus its state for the patient paying. */
export interface CheckedGiftCard extends GiftCard {
  state?: 'usable' | 'claimable' | 'owned_by_other' | 'other_location' | 'not_found' | 'no_balance';
}

export interface GiftCardTransaction {
  transactionId: number;
  giftCardId: number;
  amount: number;
  balanceBefore: number;
  balanceAfter: number;
  transactionType: GiftCardTransactionType;
  patientId: number | null;
  paymentId: number | null;
  notes: string | null;
  createdAt: string;
  /** What happened (KTD13); '' on rows written before the column existed. */
  action?: GiftCardAction | '';
  /** Why — required on adjustments; delivery details on resend / recipient change. */
  reason?: string;
}

export interface IssueGiftCardPayload {
  originalValue: number;
  issuedToPatientId?: number;
  purchaseDate?: string;
  expiresDate?: string;
  notes?: string;
  serialNumber?: string;
  title?: string;
}

export interface ReloadGiftCardPayload {
  amount: number;
  notes?: string;
}

export interface RedeemGiftCardPayload {
  amount: number;
  patientId?: number;
  paymentId?: number;
  notes?: string;
}

export interface GiftCardListParams {
  /**
   * Unified filter JSON from filterRegistry.buildApiParams — status (any of
   * active / redeemed / voided / expired) and daterange, with `filter-plus`
   * carrying per-filter negation. Same shape every other list endpoint takes.
   */
  filtersArray?: string;
  search?: string;
  page?: number;
  perPage?: number;
  /**
   * Sort field. Server-side because the list is paginated — sorting the current
   * page only would be wrong. Accepted values are whitelisted by the API
   * (GiftCardsController::LIST_SORTS); anything else falls back to purchase date.
   */
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  /** Only cards sold online to a buyer who wasn't signed in (the guest sales page). */
  guestSales?: boolean;
}

export interface PaginatedGiftCards {
  data: GiftCard[];
  meta: {
    total: number;
    page: number;
    perPage: number;
    totalPages: number;
  };
}

/** One row of redemption activity — stored value spent, from gift_cards_log. */
export interface GiftCardRedemption {
  giftCardLogID: number;
  giftCardId: number;
  serialNumber: string | null;
  title: string | null;
  redeemedAmount: number;
  /** Card balance immediately after this redemption. */
  remainingBalance: number;
  redeemedDate: string | null;
  patientId: number | null;
  patientName: string | null;
  paymentId: number | null;
  /**
   * False for redemptions written without a payment id — the POS gift-card
   * tender doesn't pass one, so they can't be attributed to a facility.
   */
  linked: boolean;
  appliedTo: string | null;
  facilityId: number | null;
  facilityName: string | null;
}

export interface GiftCardRedemptionParams {
  search?: string;
  patientId?: number;
  facilityId?: number;
  dateFrom?: string;
  dateTo?: string;
  page?: number;
  perPage?: number;
}

export interface PaginatedGiftCardRedemptions {
  data: GiftCardRedemption[];
  meta: {
    total: number;
    page: number;
    perPage: number;
    totalPages: number;
  };
}

// ─── API wrappers ─────────────────────────────────────────────────────────────

export async function listGiftCards(
  params?: GiftCardListParams,
): Promise<PaginatedGiftCards> {
  const res = await apiClient.get<PaginatedGiftCards>('/gift-cards', { params });
  return res.data;
}

export async function listGiftCardRedemptions(
  params?: GiftCardRedemptionParams,
): Promise<PaginatedGiftCardRedemptions> {
  const res = await apiClient.get<PaginatedGiftCardRedemptions>('/gift-cards/redemptions', { params });
  return res.data;
}

/** CSV of the redemption ledger for the current filters, for reconciliation. */
export function redemptionsToCsv(rows: GiftCardRedemption[]): string {
  const header = [
    'Serial', 'Date', 'Patient', 'Redeemed', 'Applied To', 'Remaining Balance', 'Facility', 'Payment ID',
  ];
  const escape = (value: string | number | null): string => {
    const s = value == null ? '' : String(value);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = rows.map((r) => [
    escape(r.serialNumber),
    escape(r.redeemedDate ? new Date(r.redeemedDate).toLocaleString() : ''),
    escape(r.patientName),
    r.redeemedAmount.toFixed(2),
    escape(r.appliedTo),
    r.remainingBalance.toFixed(2),
    escape(r.facilityName),
    escape(r.paymentId),
  ].join(','));
  return [header.join(','), ...lines].join('\r\n');
}

function generateSerial(): string {
  // e.g. GC-A3F9-2024 — unique enough for POS use
  const rand = Math.random().toString(36).toUpperCase().slice(2, 8);
  const year = new Date().getFullYear();
  return `GC-${rand}-${year}`;
}

export async function issueGiftCard(payload: IssueGiftCardPayload): Promise<GiftCard> {
  const body = {
    giftCardRefID:   payload.serialNumber?.trim() || generateSerial(),
    purchaseAmount:  payload.originalValue,
    title:           payload.title ?? null,
    patientID:       payload.issuedToPatientId ?? null,
    endDate:         payload.expiresDate ?? null,
  };
  const res = await apiClient.post<{ data: { giftCardId: number } }>('/gift-cards', body);
  const { giftCardId } = res.data.data;
  return getGiftCard(giftCardId);
}

/**
 * Look a card up by its code. With `patientId` (the patient paying) the API
 * applies the owner rule: another patient's card answers 409
 * GIFT_CARD_OWNED_BY_OTHER_PATIENT. Unknown codes answer 404, and too many
 * wrong codes 429 GIFT_CARD_CODE_LOCKED.
 */
export async function findBySerial(serialNumber: string, patientId?: number): Promise<CheckedGiftCard> {
  const res = await apiClient.get<{ data: CheckedGiftCard }>(
    `/gift-cards/check/${encodeURIComponent(serialNumber)}`,
    { params: patientId && patientId > 0 ? { patientId } : undefined },
  );
  return res.data.data;
}

export async function getGiftCard(giftCardId: number): Promise<GiftCard> {
  const res = await apiClient.get<{ data: GiftCard }>(`/gift-cards/${giftCardId}`);
  return res.data.data;
}

export async function redeemGiftCard(
  giftCardId: number,
  payload: RedeemGiftCardPayload,
): Promise<{ balance: number; redeemed: number }> {
  const res = await apiClient.post<{ data: { balance: number; redeemed: number } }>(
    `/gift-cards/${giftCardId}/redeem`,
    payload,
  );
  return res.data.data;
}

export async function voidGiftCard(giftCardId: number): Promise<{ success: boolean }> {
  const res = await apiClient.post<{ data: { success: boolean } }>(`/gift-cards/${giftCardId}/void`);
  return res.data.data;
}

export interface UpdateGiftCardPayload {
  title?: string | null;
  notes?: string | null;
  giftCardRefID?: string;
}

export async function updateGiftCard(
  giftCardId: number,
  payload: UpdateGiftCardPayload,
): Promise<GiftCard> {
  const res = await apiClient.patch<{ data: GiftCard }>(`/gift-cards/${giftCardId}`, payload);
  return res.data.data;
}

export async function reloadGiftCard(
  giftCardId: number,
  payload: ReloadGiftCardPayload,
): Promise<{ balance: number }> {
  const res = await apiClient.post<{ data: { balance: number } }>(
    `/gift-cards/${giftCardId}/reload`,
    payload,
  );
  return res.data.data;
}

export async function getTransactions(
  giftCardId: number,
): Promise<GiftCardTransaction[]> {
  const res = await apiClient.get<{ data: GiftCardTransaction[] }>(
    `/gift-cards/${giftCardId}/transactions`,
  );
  return res.data.data;
}

// ─── Staff tools for online cards (Gift Credit permission) ────────────────────

/** The full code of a card. Each call is logged in the card history as view_code. */
export async function revealGiftCardCode(giftCardId: number): Promise<string> {
  const res = await apiClient.get<{ data: { giftCardId: number; code: string } }>(
    `/gift-cards/${giftCardId}/code`,
  );
  return res.data.data.code;
}

export interface ResendGiftCardResult {
  giftCardId: number;
  sent: boolean;
  toEmail: string;
  deliveryStatus: string;
}

/** Send an unclaimed online card's stored delivery email again, to its current address. */
export async function resendGiftCardEmail(giftCardId: number): Promise<ResendGiftCardResult> {
  const res = await apiClient.post<{ data: ResendGiftCardResult }>(`/gift-cards/${giftCardId}/resend`);
  return res.data.data;
}

export interface ChangeGiftCardRecipientResult {
  giftCardId: number;
  recipientEmail: string;
  previousRecipientEmail: string | null;
  /** `error` is what staff should do; `details` is the mailer's own reason. */
  delivery: { sent: boolean; status: 'sent' | 'failed'; error: string | null; details?: string | null };
  /** Notices sent to the buyer and the previous address. */
  notices: Array<{ email: string; sent: boolean }>;
  buyer: { patientId: number | null; name: string; email: string; phone: string };
}

/** Correct an unclaimed online card's recipient email and resend the delivery email there. */
export async function changeGiftCardRecipient(
  giftCardId: number,
  recipientEmail: string,
): Promise<ChangeGiftCardRecipientResult> {
  const res = await apiClient.put<{ data: ChangeGiftCardRecipientResult }>(
    `/gift-cards/${giftCardId}/recipient`,
    { recipientEmail },
  );
  return res.data.data;
}

export interface AdjustGiftCardPayload {
  /** Signed: positive adds to the balance, negative removes. */
  amount: number;
  /** Required by the API (REASON_REQUIRED). */
  reason: string;
}

export async function adjustGiftCard(
  giftCardId: number,
  payload: AdjustGiftCardPayload,
): Promise<{ giftCardId: number; balance: number; amount: number }> {
  const res = await apiClient.post<{ data: { giftCardId: number; balance: number; amount: number } }>(
    `/gift-cards/${giftCardId}/adjust`,
    payload,
  );
  return res.data.data;
}

export interface VoidRefundGiftCardResult {
  giftCardId: number;
  voidedBalance: number;
  refundAmount: number;
  refundTransactionId: number | null;
  forwardRefundId: string | null;
  salePaymentId: number;
}

/**
 * Void an unclaimed online card and refund the buyer in full through the sale
 * payment. A claimed card answers 409 GIFT_CARD_CLAIMED.
 */
export async function voidRefundGiftCard(
  giftCardId: number,
  reason?: string,
): Promise<VoidRefundGiftCardResult> {
  const res = await apiClient.post<{ data: VoidRefundGiftCardResult }>(
    `/gift-cards/${giftCardId}/void-refund`,
    { reason: reason ?? '' },
  );
  return res.data.data;
}

export interface GiftCardListStats {
  total: number;
  active: number;
  /** Cards with no balance left that were actually spent — not voided. */
  redeemed?: number;
  /** Zero-balance cards that were never redeemed. */
  voided: number;
  expired?: number;
  /** Outstanding stored value: the sum of all positive balances. */
  totalBalance: number;
}

export function getGiftCardStats(): Promise<GiftCardListStats> {
  return apiClient
    .get<{ data: GiftCardListStats }>('/gift-cards/stats')
    .then((r) => r.data.data);
}
